import { useStoredParams } from "@/src/context/data-table-search-context";

interface UseTableSortOptions {
  path: string;
  defaultSortBy?: string;
  defaultSortOrder?: "asc" | "desc";
}

/**
 * Hook that parses sorting from the data table search context.
 * Extracts sortBy and sortOrder from the URL's sort parameter.
 *
 * @param options.path - The base path to match against (e.g., "/leads", "/patients", "/tasks")
 * @param options.defaultSortBy - Default sort field if no sort is present (defaults to "createdAt")
 * @param options.defaultSortOrder - Default sort order if no sort is present (defaults to "desc")
 * @returns Object with sortBy and sortOrder
 */
export function useTableSort({ path, defaultSortBy = "createdAt", defaultSortOrder = "desc" }: UseTableSortOptions) {
  const searchParams = useStoredParams(path);

  return (() => {
    const defaultSort = {
      sortBy: defaultSortBy,
      sortOrder: defaultSortOrder as "asc" | "desc",
    };

    if (!searchParams) {
      return defaultSort;
    }

    const sortParam = searchParams.get("sort");

    if (!sortParam) {
      return defaultSort;
    }

    // sort param follows the format: id:true,id2:false where true is desc - we only use the first entry
    const first = sortParam.split(",")[0];
    const [id, isDesc] = first.split(":");

    return {
      sortBy: id || defaultSortBy,
      sortOrder: isDesc === "true" ? ("desc" as const) : ("asc" as const),
    };
  })();
}
